/**
 * Branches (Filiallar) API.
 * Backend: snake_case, Frontend: camelCase.
 */
import api from './api';
import { endpoints } from './endpoints';
import { buildBranchShopSlug } from './marketingApi';

/**
 * Laravel `Branch::publicShopPathSegment()` bilan bir xil: shop_subdomain → slug → nom-id.
 * `shop_path_segment` maydoni eski backendda bo‘lmasa ham mijoz–kassir havolasi mos tushadi.
 */
function computePublicShopPathSegment(raw) {
  if (!raw || typeof raw !== 'object') return '';
  const fromApi = String(raw.shop_path_segment ?? raw.shopPathSegment ?? '')
    .trim()
    .toLowerCase();
  if (fromApi) return fromApi;
  const sub = String(raw.shop_subdomain ?? raw.shopSubdomain ?? '').trim().toLowerCase();
  if (sub) return sub;
  const slug = raw.slug != null ? String(raw.slug).trim().toLowerCase() : '';
  if (slug) return slug;
  return String(buildBranchShopSlug({ id: raw.id, name: raw.name })).toLowerCase();
}

function normalizeBranch(raw) {
  if (!raw) return null;
  const regionVal = raw.region ?? raw.region_name ?? raw.regionName ?? '';
  const regionObj = typeof regionVal === 'object' && regionVal !== null ? regionVal : null;
  const regionCode = regionObj ? null : (regionVal != null ? String(regionVal) : '');
  const regionId = String(
    raw.region_id ??
      raw.regionId ??
      (regionObj && (regionObj.id ?? regionObj.code)) ??
      regionCode ??
      ''
  );
  const regionName =
    regionObj?.name_uz ??
    regionObj?.name_ru ??
    regionObj?.name ??
    raw.region_label ??
    raw.regionLabel ??
    regionCode ??
    '';

  const tenantObj = raw.tenant && typeof raw.tenant === 'object' ? raw.tenant : null;
  const tenantId = String(
    raw.tenant_id ??
      raw.tenantId ??
      tenantObj?.id ??
      (tenantObj?.tenant_id ?? tenantObj?.tenantId) ??
      ''
  );
  const tenantName =
    raw.tenant_name ??
    raw.tenantName ??
    tenantObj?.company_name ??
    tenantObj?.companyName ??
    tenantObj?.name ??
    '';
  const tenantPhone =
    raw.tenant_phone ??
    raw.tenantPhone ??
    tenantObj?.phone ??
    tenantObj?.contact_phone ??
    tenantObj?.contactPhone ??
    '';

  const telegramGroupId = String(
    raw.telegram_group_id ?? raw.telegramGroupId ?? raw.telegram_chat_id ?? raw.telegramChatId ?? '',
  ).trim();
  const telegramGroupTitle = String(
    raw.telegram_group_title ?? raw.telegramGroupTitle ?? raw.telegram_chat_title ?? '',
  ).trim();

  const deliveryRaw = raw.delivery_enabled ?? raw.deliveryEnabled;
  const shopPathSeg = computePublicShopPathSegment(raw);
  return {
    id: String(raw.id),
    name: raw.name ?? '',
    slug: raw.slug != null ? String(raw.slug) : '',
    shopSubdomain: String(raw.shop_subdomain ?? raw.shopSubdomain ?? '').trim(),
    shopPathSegment: shopPathSeg,
    deliveryEnabled: deliveryRaw !== undefined && deliveryRaw !== null ? Boolean(deliveryRaw) : undefined,
    address: raw.address ?? '',
    responsibleName: raw.responsible_name ?? raw.responsibleName ?? '',
    responsiblePhone: raw.responsible_phone ?? raw.responsiblePhone ?? '',
    tenantId,
    tenantName,
    tenantPhone,
    regionId,
    region: regionName || regionCode,
    telegramGroupId,
    telegramGroupTitle,
  };
}

export async function getBranches(params = {}) {
  const res = await api.get(endpoints.branches, { params });
  let list = res?.data;
  if (list && !Array.isArray(list) && Array.isArray(list.data)) list = list.data;
  if (!Array.isArray(list)) list = [];
  return list.map(normalizeBranch).filter(Boolean);
}

export async function createBranch(payload) {
  const body = {
    name: payload.name?.trim() ?? '',
    address: payload.address?.trim() ?? '',
    responsible_name: payload.responsibleName?.trim() ?? '',
    responsible_phone: payload.responsiblePhone?.trim() ?? '',
  };
  if (payload.tenantId != null && payload.tenantId !== '') {
    body.tenant_id = payload.tenantId;
  }
   if (payload.regionId != null && payload.regionId !== '') {
    body.region_id = payload.regionId;
  }
  const { data } = await api.post(endpoints.branches, body);
  return normalizeBranch(data?.data ?? data);
}

export async function updateBranch(id, payload) {
  const body = {};
  if (payload.name !== undefined) body.name = payload.name?.trim() ?? '';
  if (payload.address !== undefined) body.address = payload.address?.trim() ?? '';
  if (payload.responsibleName !== undefined) body.responsible_name = payload.responsibleName?.trim() ?? '';
  if (payload.responsiblePhone !== undefined) body.responsible_phone = payload.responsiblePhone?.trim() ?? '';
  if (payload.tenantId !== undefined) body.tenant_id = payload.tenantId || null;
  if (payload.regionId !== undefined) body.region_id = payload.regionId || null;
  if (payload.shopSubdomain !== undefined) {
    const s = payload.shopSubdomain === null || payload.shopSubdomain === '' ? '' : String(payload.shopSubdomain).trim().toLowerCase();
    body.shop_subdomain = s === '' ? null : s;
  }
  const { data } = await api.put(endpoints.branchById(id), body);
  return normalizeBranch(data?.data ?? data);
}

export async function deleteBranch(id) {
  await api.delete(endpoints.branchById(id));
}

/**
 * Bitta filial bo‘yicha kengaytirilgan ma'lumot (cashierlar, sotuv hajmi, ombor holati).
 * Backend: GET /api/branches/:id — filial + stats + cashiers + warehouse.
 */
export async function getBranchDetail(id) {
  if (!id) return null;
  const res = await api.get(endpoints.branchById(id));
  let raw = res?.data ?? res;
  /** Ba'zi proksi / o‘ralmalar: { data: { data: filial } } */
  if (
    raw &&
    typeof raw === 'object' &&
    !Array.isArray(raw) &&
    raw.id == null &&
    raw.data &&
    typeof raw.data === 'object' &&
    !Array.isArray(raw.data) &&
    raw.data.id != null
  ) {
    raw = raw.data;
  }
  if (!raw) return null;
  const branch = normalizeBranch(raw);

  const statsRaw = raw.stats ?? raw.statistics ?? raw.branch_stats ?? null;
  const stats = statsRaw && typeof statsRaw === 'object'
    ? {
        ordersMonth: Number(
          statsRaw.orders_month ??
            statsRaw.ordersMonth ??
            statsRaw.monthly_orders ??
            0
        ),
        revenueMonth: Number(
          statsRaw.revenue_month ??
            statsRaw.revenueMonth ??
            statsRaw.monthly_revenue ??
            0
        ),
        revenueTotal: Number(
          statsRaw.revenue_total ??
            statsRaw.revenueTotal ??
            statsRaw.total_revenue ??
            0
        ),
        lastOrderAt:
          statsRaw.last_order_at ??
          statsRaw.lastOrderAt ??
          statsRaw.last_order ??
          null,
      }
    : null;

  const cashiersRaw =
    raw.cashiers ??
    raw.branch_cashiers ??
    raw.cashiers_list ??
    [];
  const cashiers = Array.isArray(cashiersRaw)
    ? cashiersRaw
        .map((c) => {
          if (!c || typeof c !== 'object') return null;
          return {
            id: String(c.id ?? c.user_id ?? ''),
            name: c.name ?? c.full_name ?? '',
            phone: c.phone ?? c.contact_phone ?? '',
            status: c.status ?? 'active',
            ordersMonth: Number(
              c.orders_month ??
                c.ordersMonth ??
                c.monthly_orders ??
                0
            ),
            revenueMonth: Number(
              c.revenue_month ??
                c.revenueMonth ??
                c.monthly_revenue ??
                0
            ),
            lastShiftAt:
              c.last_shift_at ??
              c.lastShiftAt ??
              c.last_activity_at ??
              null,
          };
        })
        .filter(Boolean)
    : [];

  const warehouseRaw =
    raw.warehouse ??
    raw.stock_summary ??
    raw.inventory ??
    null;
  const warehouse =
    warehouseRaw && typeof warehouseRaw === 'object'
      ? {
          productsCount: Number(
            warehouseRaw.products_count ??
              warehouseRaw.productsCount ??
              warehouseRaw.items_count ??
              0
          ),
          totalStock: Number(
            warehouseRaw.total_stock ??
              warehouseRaw.totalStock ??
              warehouseRaw.stock_quantity ??
              0
          ),
          totalStockValue: Number(
            warehouseRaw.total_stock_value ??
              warehouseRaw.totalStockValue ??
              warehouseRaw.stock_value ??
              0
          ),
        }
      : null;

  return {
    ...branch,
    stats,
    cashiers,
    warehouse,
  };
}
